import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import type { LizardSim } from '../sim/Lizard';
import type { Terrain } from '../game/Terrain';
import { readTune } from '../ui/TuneStore';

import { assetUrl as _assetUrl } from '../ui/AssetMap';
const MODEL_SLOT = 'lizard';
const MODEL_URL_DEFAULT = '/assets/lizard.glb';
const TUNE_ID = 'lizard';
const TUNE_DEFAULTS = { scale: 0.1, rotX: 0, rotY: 0, rotZ: 0, offY: 0 };

export class LizardMesh {
  readonly group = new THREE.Group();
  private avatarRoot: THREE.Group | null = null;
  private mixer: THREE.AnimationMixer | null = null;
  private readonly forward = new THREE.Vector3();
  private readonly up = new THREE.Vector3();

  constructor(private sim: LizardSim, private terrain: Terrain) {
    this.group.name = 'lizard';
    this.group.visible = false;
    void this.load();
  }

  private async load(): Promise<void> {
    try {
      const gltf = await new GLTFLoader().loadAsync(_assetUrl(MODEL_SLOT, MODEL_URL_DEFAULT));
      const tune = readTune(TUNE_ID, TUNE_DEFAULTS);
      const cloned = skeletonClone(gltf.scene);
      cloned.rotation.set(tune.rotX, tune.rotY, tune.rotZ);
      cloned.scale.setScalar(tune.scale);
      cloned.updateMatrixWorld(true);
      const bb = new THREE.Box3().setFromObject(cloned);
      const c = new THREE.Vector3(); bb.getCenter(c);
      cloned.position.set(-c.x, -bb.min.y + tune.offY, -c.z);
      cloned.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        m.castShadow = true;
        m.receiveShadow = true;
        m.frustumCulled = false;
      });
      const root = new THREE.Group();
      root.add(cloned);
      root.matrixAutoUpdate = false;
      this.group.add(root);
      this.avatarRoot = root;
      if (gltf.animations.length > 0) {
        this.mixer = new THREE.AnimationMixer(cloned);
        const act = this.mixer.clipAction(gltf.animations[0]);
        act.play();
      }
    } catch (err) {
      console.warn('[LizardMesh] load failed:', err);
    }
  }

  update(dt: number): void {
    const s = this.sim.state;
    if (!s.alive || !this.avatarRoot) {
      this.group.visible = false;
      return;
    }
    this.group.visible = true;
    this.mixer?.update(dt);

    // Terrain-aligned basis, same pattern as the grasshopper/spider.
    this.terrain.getNormal(s.x, s.z, this.up);
    this.forward.set(Math.cos(s.heading), 0, Math.sin(s.heading));
    this.forward.addScaledVector(this.up, -this.forward.dot(this.up));
    if (this.forward.lengthSq() < 1e-6) {
      this.forward.set(1, 0, 0);
      this.forward.addScaledVector(this.up, -this.forward.dot(this.up));
    }
    this.forward.normalize();
    const bx = this.forward.y * this.up.z - this.forward.z * this.up.y;
    const by = this.forward.z * this.up.x - this.forward.x * this.up.z;
    const bz = this.forward.x * this.up.y - this.forward.y * this.up.x;
    const e = this.avatarRoot.matrix.elements;
    e[0] = this.forward.x; e[1] = this.forward.y; e[2] = this.forward.z; e[3] = 0;
    e[4] = this.up.x;      e[5] = this.up.y;      e[6] = this.up.z;      e[7] = 0;
    e[8] = bx;             e[9] = by;             e[10] = bz;            e[11] = 0;
    e[12] = s.x;           e[13] = s.y + 0.01;    e[14] = s.z;           e[15] = 1;
    this.avatarRoot.matrixWorldNeedsUpdate = true;
  }
}
